import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen } from 'lucide-react';
import { StarRating } from '@/components/StarRating';
import { useNavigate } from 'react-router-dom';

interface MessagePreviewCardProps {
  sharePayload: {
    kind: 'book' | 'review';
    book_id?: string;
    review_id?: string;
    book?: {
      id: string;
      title: string;
      authors: string[];
      cover_url?: string;
    };
    review?: {
      id: string;
      text: string;
      rating: number;
    };
  };
  chatId?: string;
}

export function MessagePreviewCard({ sharePayload, chatId }: MessagePreviewCardProps) {
  const navigate = useNavigate();

  if (sharePayload.kind === 'book') {
    return (
      <Card className="mt-2 max-w-md">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-16 bg-muted rounded flex-shrink-0 flex items-center justify-center">
              {sharePayload.book?.cover_url ? (
                <img 
                  src={sharePayload.book.cover_url} 
                  alt={sharePayload.book.title}
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <BookOpen className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm truncate">
                {sharePayload.book?.title || 'Unknown Book'}
              </h4>
              <p className="text-xs text-muted-foreground truncate">
                {sharePayload.book?.authors?.join(', ') || 'Unknown Author'}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => navigate(`/book/${sharePayload.book_id}?utm=share${chatId ? `&chat=${chatId}` : ''}`)}
              >
                View Book
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (sharePayload.kind === 'review') {
    return (
      <Card className="mt-2 max-w-md">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <StarRating 
                value={sharePayload.review?.rating || 0} 
                readonly 
                size="sm" 
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm truncate">
                {sharePayload.book?.title || 'Book Review'}
              </h4>
              {sharePayload.review?.text && (
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                  {sharePayload.review.text}
                </p>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => navigate(`/book/${sharePayload.book_id}?review=${sharePayload.review_id}&utm=share${chatId ? `&chat=${chatId}` : ''}`)}
              >
                Read Review
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}